import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Play, Pause, Loader2, Database, AlertCircle, CheckCircle, Wrench } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Panel de migración Yahoo (bloque crítico 2022 → hoy).
 * Muestra estado por cuenta IMAP + controles start/pause.
 */
export default function MigracionYahooPanel({ accounts = [] }) {
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionAcc, setActionAcc] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [lastReset, setLastReset] = useState(null); // {analizados, reseteados, at}

  const yahooAccounts = accounts.filter(a => a.provider === 'yahoo' || a.auth_type === 'imap_smtp');

  const fetchStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/bandeja-unificada/admin/migracion-yahoo/status`);
      setStatuses(r.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchStatus();
    const iv = setInterval(fetchStatus, 5000);
    return () => clearInterval(iv);
  }, [fetchStatus]);

  const start = async (acc) => {
    if (!window.confirm(`¿Iniciar migración desde 2022 hasta hoy para ${acc.email}?\n\nLa migración corre mes a mes con pausa de 60s entre meses. Puede tardar varios días.`)) return;
    setActionAcc(acc.id);
    try {
      await axios.post(`${API}/bandeja-unificada/admin/migracion-yahoo/start?account_id=${acc.id}`);
      fetchStatus();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
    setActionAcc(null);
  };

  const pause = async (acc) => {
    setActionAcc(acc.id);
    try {
      await axios.post(`${API}/bandeja-unificada/admin/migracion-yahoo/pause?account_id=${acc.id}`);
      fetchStatus();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
    setActionAcc(null);
  };

  const findStatus = (acc_id) => statuses.find(s => s.account_id === acc_id);

  const resetHidrataciones = async () => {
    const ok = window.confirm(
      '¿Resetear hidrataciones vacías?\n\n' +
      'Busca correos que quedaron marcados como hidratados pero cuyo cuerpo quedó vacío ' +
      '(por la colisión de _upsert_message sobre headers ya migrados) y los re-marca ' +
      '"pendiente" para que se re-hidraten al abrirlos.\n\n' +
      'Alcance: todas las cuentas (hasta 500 mensajes por corrida).'
    );
    if (!ok) return;
    setResetting(true);
    try {
      const r = await axios.post(`${API}/bandeja-unificada/admin/reset-hidrataciones-vacias?limit=500`);
      const data = r.data || {};
      setLastReset({
        analizados: data.analizados || 0,
        reseteados: data.reseteados || 0,
        at: new Date().toLocaleTimeString('es-AR'),
      });
      toast.success(
        `Reset OK · ${data.reseteados || 0} reseteados / ${data.analizados || 0} analizados`,
        { description: (data.muestra || []).length > 0
            ? `Ej: "${(data.muestra[0].subject || '(sin asunto)').slice(0, 60)}"`
            : 'Sin correos con cuerpo vacío detectados.' }
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error reseteando hidrataciones');
    }
    setResetting(false);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Database className="w-5 h-5 text-purple-600" /> Migración de historial Yahoo (2022 → hoy)
            </CardTitle>
            <p className="text-xs text-gray-600 mt-1">
              Descarga headers mes a mes hacia atrás (60s de pausa entre meses para no gatillar Yahoo).
              Los cuerpos se descargan on-demand al abrir cada mail.
            </p>
          </div>
          <div className="shrink-0 flex flex-col items-end gap-1">
            <Button
              size="sm"
              variant="outline"
              onClick={resetHidrataciones}
              disabled={resetting}
              data-testid="btn-reset-hidrataciones-vacias"
              title="Re-marca como pendientes los correos hidratados que quedaron con cuerpo vacío"
            >
              {resetting ? (
                <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Reseteando...</>
              ) : (
                <><Wrench className="w-4 h-4 mr-1" /> Resetear hidrataciones vacías</>
              )}
            </Button>
            {lastReset && (
              <span
                className="text-[11px] text-gray-500"
                data-testid="reset-hidrataciones-resumen"
              >
                Último: {lastReset.reseteados}/{lastReset.analizados} · {lastReset.at}
              </span>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? <Loader2 className="w-5 h-5 animate-spin text-purple-600" /> : (
          yahooAccounts.length === 0 ? (
            <p className="text-sm text-gray-500 italic">No hay cuentas IMAP configuradas.</p>
          ) : yahooAccounts.map(acc => {
            const st = findStatus(acc.id);
            const running = st?.estado === 'running';
            const paused = st?.estado === 'paused';
            const completed = st?.estado === 'completed';
            return (
              <div key={acc.id} className="border rounded-lg p-3" data-testid={`migracion-cuenta-${acc.id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900 truncate">{acc.email}</p>
                    <div className="text-xs mt-1 flex items-center gap-2 flex-wrap">
                      {!st && <span className="text-gray-500">Sin migrar</span>}
                      {running && <span className="inline-flex items-center gap-1 text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full">
                        <Loader2 className="w-3 h-3 animate-spin" /> Procesando {st.ventana_actual}
                      </span>}
                      {paused && <span className="inline-flex items-center gap-1 text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                        <Pause className="w-3 h-3" /> Pausada en {st.ultimo_mes_procesado}
                      </span>}
                      {completed && <span className="inline-flex items-center gap-1 text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
                        <CheckCircle className="w-3 h-3" /> Completada
                      </span>}
                      {st?.ultimo_error && <span className="inline-flex items-center gap-1 text-red-700 bg-red-50 px-2 py-0.5 rounded-full" title={st.ultimo_error}>
                        <AlertCircle className="w-3 h-3" /> Último error
                      </span>}
                      {st?.mensajes_procesados_total > 0 && (
                        <span className="text-gray-600">{st.mensajes_procesados_total.toLocaleString('es-AR')} mensajes procesados</span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    {running ? (
                      <Button size="sm" variant="outline" onClick={() => pause(acc)} disabled={actionAcc === acc.id} data-testid={`btn-pause-${acc.id}`}>
                        <Pause className="w-4 h-4 mr-1" /> Pausar
                      </Button>
                    ) : (
                      <Button size="sm" className="bg-purple-600 hover:bg-purple-700" onClick={() => start(acc)} disabled={actionAcc === acc.id} data-testid={`btn-start-${acc.id}`}>
                        {actionAcc === acc.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Play className="w-4 h-4 mr-1" /> {paused ? 'Reanudar' : 'Iniciar'}</>}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}
